import React, { memo } from "react";
import { GatsbyImage, getImage, withArtDirection } from "gatsby-plugin-image";
import styled from "styled-components";

const BREAKPOINTS = [640, 768, 1024];

const constructSrcSet = (cdnURL, width) => {
  const widths = BREAKPOINTS.filter((w) => w < width);
  const params = [`f=auto`, `q=85`];

  let url = new URL(cdnURL);
  const pathname = url.pathname;

  return [...widths, width]
    .map((w) => {
      const paramsString = [...params, `width=${w}`].join(",");
      url.pathname = `/cdn-cgi/image/${paramsString}` + pathname;
      return `${url.toString()} ${w}w`;
    })
    .join(",\n");
};

export const fullWidth = (image) => {
  const cdnURL = image.cdnURL;
  const srcset = constructSrcSet(cdnURL, image.width);

  return {
    layout: "fullWidth",
    placeholder: {
      fallback: image.formats?.base64?.data,
    },
    images: {
      fallback: {
        src: cdnURL,
        srcSet: srcset,
        sizes: "100vw",
      },
      sources: [
        {
          srcSet: srcset,
          type: "image/webp",
          sizes: "100vw",
        },
      ],
    },
    width: 1,
    height: parseFloat(image.height) / image.width,
  };
};

// https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-plugin-image/#withartdirection
const artDirection = (object, layout) => {
  const mobileFullWidth = fullWidth(object.mobile);
  const desktopFullWidth = fullWidth(object.desktop);

  return withArtDirection(getImage(mobileFullWidth), [
    {
      media: "(min-width: 768px)",
      image: getImage(desktopFullWidth),
    },
  ]);
};

const GatsbyArtDirectedImage = ({ image, ...props }) => (
  <GatsbyImage image={artDirection(image)} {...props} />
);

// https://medium.com/swlh/art-direction-with-gatsby-image-the-aspect-ratio-bug-8281f7a8594c
export const ArtDirectedImage = styled(GatsbyArtDirectedImage)`
  > div:first-child {
    @media (min-width: 768px) {
      padding-top: ${(props) =>
        `${
          parseFloat(props.image.desktop.height * 100) /
          props.image.desktop.width
        }% !important`};
    }
  }
`;

export const MemoizedArtDirectedImage = memo(ArtDirectedImage);

// When an image is positioned using absolute positioning, what we get is an
// image which is removed from dom flow. We can put a div with the same dimensions
// as that of the image, and put the absolute positioned image on top of this
// div. Now, the iamge looks as if it is relatively positioned
export const DivToMakeAbsoluteImageLookRelative = styled.div`
  {
    z-index: -10;
    padding-top: ${(props) =>
      `${
        parseFloat(props.image.mobile.height * 100) / props.image.mobile.width
      }%`};
    }

    @media (min-width: 768px) {
      padding-top: ${(props) =>
        `${
          parseFloat(props.image.desktop.height * 100) /
          props.image.desktop.width
        }% !important`};
    }
  }
`;
